// Shared "is this KPI strip all zeros?" helpers.
//
// KPI strips across the app (PageHeader summaryStats on HR / Operations /
// Messaging, the staff dashboard's StaffKpiCards) all go all-zero on a small
// or brand-new org and render as a wall of "0"s. These helpers let each strip
// detect that state and collapse to a slim nudge instead.

/**
 * Treats a single stat value as "empty" (i.e. nothing to show yet):
 * - numbers: exactly 0
 * - null / undefined: empty
 * - strings: empty once currency symbols, commas, and whitespace are stripped
 *   ("$0", "0", "", "—" all read as empty); a non-numeric string like "Active"
 *   is NOT empty.
 *
 * Note: this is purely about the value. Error placeholders (e.g. "—" shown when
 * a fetch fails) also read as empty here, so callers that can render an error
 * state must guard on that separately before collapsing to a happy nudge.
 */
export function isValueEmpty(value: string | number | null | undefined): boolean {
  if (value == null) return true;
  if (typeof value === "number") return value === 0;
  const cleaned = value.replace(/[^0-9.-]/g, "");
  if (cleaned === "" || cleaned === "-") return true;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed === 0 : false;
}

/** True when every value in the strip is empty (see {@link isValueEmpty}). */
export function allValuesEmpty(
  values: Array<string | number | null | undefined>,
): boolean {
  return values.length > 0 && values.every(isValueEmpty);
}
